import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import Sidebar from "../components/Sidebar";
import TopNavbar from "../components/TopNavbar";
import Breadcrumbs from "../components/Breadcrumbs";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography } from "@mui/material";

const MainLayout: React.FC = () => {
  const location = useLocation();
  const { sidebarOpen, sidebarCollapsed } = useSelector(
    (state: RootState) => state.ui
  );

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300">
      {/* Sidebar - Always visible on desktop */}
      <motion.div
        className="hidden lg:block relative"
        initial={{ x: -280 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        key="desktop-sidebar"
      >
        <Sidebar />
      </motion.div>

      {/* Mobile Sidebar */}
      <AnimatePresence mode="wait">
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ x: -280, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -280, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="fixed inset-y-0 left-0 z-50 lg:hidden"
              key="mobile-sidebar"
            >
              <Sidebar />
            </motion.div>
            {/* Mobile sidebar overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => {
                // This will be handled by the Sidebar component
              }}
            />
          </>
        )}
      </AnimatePresence>

      {/* Main content area */}
      <motion.div
        className="flex-1 flex flex-col overflow-hidden"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Top Navigation */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <TopNavbar />
        </motion.div>

        {/* Breadcrumbs */}
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-3 border-b border-gray-200/50 dark:border-gray-700/50 bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm"
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Breadcrumbs />
        </motion.div>

        {/* Page Content */}
        <main className="flex-1 overflow-auto min-h-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="min-h-full"
          >
            <div className="min-h-full">
              <Outlet />
            </div>
          </motion.div>
        </main>

        {/* Professional Footer */}
        <Box
          component="footer"
          className="professional-footer footer-gradient-executive"
          sx={{
            color: "white",
            py: 1.0,
            textAlign: "center",
            position: "relative",
            zIndex: 1,
            flexShrink: 0,
            boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.2)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
            },
          }}
        >
          <Box sx={{ maxWidth: "1200px", mx: "auto", px: 3 }}>
            {/* <Typography 
              variant="body2" 
              className="footer-text"
              sx={{ 
                fontWeight: 600,
                letterSpacing: '0.8px',
                mb: 1,
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                color: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              Manufacturing Management System
            </Typography> */}
            <Typography
              variant="caption"
              sx={{
                opacity: 0.8,
                fontSize: "0.75rem",
                letterSpacing: "0.4px",
                display: "block",
                fontStyle: "italic",
                color: "rgba(255, 255, 255, 0.8)",
              }}
            >
              © 2025 All rights reserved.
            </Typography>
          </Box>
        </Box>
      </motion.div>
    </div>
  );
};

export default MainLayout;
